import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { api } from '../api/client';
import { useCapabilities } from '../lib/auth';
import { Button, Callout, CheckLine, Dialog, ErrorNote, SelectField, TextField, useToast } from './ui';

/**
 * Product options.
 *
 * A product declares the options it understands (on the Products page); staff
 * set them per company on that company's licence; the product receives the
 * values in `license.settings` at every sign-in and refresh. Heron never
 * interprets them — "show_cost" means something only to the 360 Dashboard.
 */

export type SettingType = 'boolean' | 'text' | 'number' | 'select' | 'secret';
export type SettingValue = boolean | string | number | null;

export interface SettingDefinition {
  key: string;
  label: string;
  type: SettingType;
  help?: string;
  options?: string[];
  default?: SettingValue;
}

const TYPE_LABEL: Record<SettingType, string> = {
  boolean: 'Tick box',
  text: 'Text',
  number: 'Number',
  select: 'Pick from a list',
  secret: 'Secret',
};

/** One clear line explaining each type, shown while editing. */
const TYPE_DESC: Record<SettingType, string> = {
  boolean: 'An on/off switch — e.g. whether cost figures are shown.',
  text: 'A short piece of text — e.g. a server address.',
  number: 'A number.',
  select: 'One choice from a fixed list you define.',
  secret: 'A password or key. Staff set it but never see it again; stored encrypted and sent only to the product at sign-in.',
};

/** A tiny two-or-three-way toggle built on the existing `.segmented` style. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ==========================================================================
   Products page: declare a product's options
   ========================================================================== */

interface DraftRow {
  key: string;
  label: string;
  type: SettingType;
  help: string;
  options: string; // comma separated, for select
  default: string; // as typed; converted on save
  keyTouched: boolean; // once staff edit the key, stop auto-filling it
}

const toDraft = (d: SettingDefinition): DraftRow => ({
  key: d.key,
  label: d.label,
  type: d.type,
  help: d.help ?? '',
  options: (d.options ?? []).join(', '),
  default: d.default === null || d.default === undefined ? '' : String(d.default),
  keyTouched: true,
});

function fromDraft(row: DraftRow): SettingDefinition {
  const base = {
    key: row.key.trim(),
    label: row.label.trim(),
    ...(row.help.trim() ? { help: row.help.trim() } : {}),
  };
  switch (row.type) {
    case 'boolean':
      return { ...base, type: 'boolean', default: row.default === 'true' };
    case 'number':
      return { ...base, type: 'number', default: row.default.trim() === '' ? null : Number(row.default) };
    case 'select': {
      const options = row.options.split(',').map((o) => o.trim()).filter(Boolean);
      return { ...base, type: 'select', options, default: row.default.trim() || null };
    }
    case 'secret':
      return { ...base, type: 'secret' };
    default:
      return { ...base, type: 'text', default: row.default };
  }
}

/** Name → key suggestion: "Show cost figures" → "show_cost". */
function suggestKey(label: string): string {
  const base = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  if (!base) return '';
  return /^[a-z]/.test(base) ? base : `opt_${base}`.slice(0, 48);
}

const KEY_RE = /^[a-z][a-z0-9_]{0,47}$/;

interface Template {
  label: string;
  make: () => DraftRow;
}

const TEMPLATES: Template[] = [
  {
    label: 'Tick box',
    make: () => ({
      key: '',
      label: '',
      type: 'boolean',
      help: '',
      options: '',
      default: 'false',
      keyTouched: false,
    }),
  },
  {
    label: 'Text',
    make: () => ({ key: '', label: '', type: 'text', help: '', options: '', default: '', keyTouched: false }),
  },
  {
    label: 'Secret (password)',
    make: () => ({ key: '', label: '', type: 'secret', help: '', options: '', default: '', keyTouched: false }),
  },
  {
    label: 'Pick from list',
    make: () => ({ key: '', label: '', type: 'select', help: '', options: '', default: '', keyTouched: false }),
  },
];

/** Default shown in the compact list, per type. */
function draftDefaultLabel(row: DraftRow): string {
  if (row.type === 'secret') return 'set per company';
  if (row.type === 'boolean') return row.default === 'true' ? 'On' : 'Off';
  if (row.type === 'select') return row.default.trim() || '—';
  return row.default.trim() || '—';
}

export function ProductOptionsDialog({
  productId,
  productName,
  open,
  onClose,
}: {
  productId: string;
  productName: string;
  open: boolean;
  onClose: () => void;
}) {
  const canEdit = useCapabilities().canWrite;
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  // Built in, not an option: every product has it, on unless turned off.
  const [singleSession, setSingleSession] = useState(true);

  const schema = useQuery({
    queryKey: ['product-settings-schema', productId],
    queryFn: ({ signal }) =>
      api.get<{ schema: SettingDefinition[]; singleSession: boolean }>(
        `/api/staff/products/${productId}/settings-schema`,
        signal,
      ),
    enabled: open,
  });

  useEffect(() => {
    if (open && schema.data) {
      setRows(schema.data.schema.map(toDraft));
      setSingleSession(schema.data.singleSession);
      setSelected(null);
    }
  }, [open, schema.data]);

  // Per-row validity, plus the duplicate-key check across the whole list.
  const errors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) counts.set(row.key.trim(), (counts.get(row.key.trim()) ?? 0) + 1);
    return rows.map((row): { label?: string; key?: string; options?: string } => {
      const e: { label?: string; key?: string; options?: string } = {};
      if (!row.label.trim()) e.label = 'A name is required.';
      const key = row.key.trim();
      if (!key) e.key = 'A key is required.';
      else if (!KEY_RE.test(key)) e.key = 'Lowercase letters, digits and underscores; start with a letter.';
      else if ((counts.get(key) ?? 0) > 1) e.key = 'Another option already uses this key.';
      if (row.type === 'select' && row.options.split(',').map((o) => o.trim()).filter(Boolean).length === 0) {
        e.options = 'Add at least one choice.';
      }
      return e;
    });
  }, [rows]);

  const hasErrors = errors.some((e) => e.label || e.key || e.options);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/staff/products/${productId}/settings-schema`, {
        schema: rows.map(fromDraft),
        singleSession,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['product-settings-schema', productId] });
      void queryClient.invalidateQueries({ queryKey: ['license-settings'] });
      push({ tone: 'success', title: `${productName} options saved` });
      onClose();
    },
  });

  const update = (i: number, patch: Partial<DraftRow>) =>
    setRows((current) => current.map((row, j) => (j === i ? { ...row, ...patch } : row)));

  const updateLabel = (i: number, label: string) =>
    setRows((current) =>
      current.map((row, j) =>
        j === i ? { ...row, label, key: row.keyTouched ? row.key : suggestKey(label) } : row,
      ),
    );

  const move = (i: number, delta: number) =>
    setRows((current) => {
      const next = [...current];
      const j = i + delta;
      if (j < 0 || j >= next.length) return current;
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  const addTemplate = (template: Template) => {
    setRows((current) => [...current, template.make()]);
    setSelected(rows.length); // the new last row
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        save.reset();
        onClose();
      }}
      title={`${productName} options`}
      hint="Switches this product reads per company — for example whether cost figures are shown. Staff set the values on each company’s licence; the product receives them at every sign-in."
      wide
    >
      <div className="stack">
        {schema.isError && <ErrorNote error={schema.error} />}
        {save.isError && <ErrorNote error={save.error} />}
        {!canEdit && (
          <Callout tone="info">Your role is read-only, so these options cannot be changed here.</Callout>
        )}

        <CheckLine
          title="Single-session login"
          description="One device at a time: signing in on a second device asks to sign the first one out. Untick to let a login be open on several devices at once."
          checked={singleSession}
          disabled={!canEdit}
          onChange={setSingleSession}
        />

        {canEdit && (
          <div className="opt-quickadd stack stack--tight">
            <div className="opt-add">
              <span className="small muted">Add an option:</span>
              <div className="row" style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}>
                {TEMPLATES.map((template) => (
                  <Button key={template.label} size="sm" onClick={() => addTemplate(template)}>
                    + {template.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {rows.length === 0 && !schema.isLoading && (
          <p className="small muted">No options yet. Add one above.</p>
        )}

        {rows.length > 0 && (
          <ul className="opt-list">
            {rows.map((row, i) => {
              const rowErr = errors[i]!;
              const invalid = Boolean(rowErr.label || rowErr.key || rowErr.options);
              const isOpen = selected === i;
              return (
                <li key={i} className={`opt-item${isOpen ? ' opt-item--open' : ''}`}>
                  <div className="opt-item__head">
                    {canEdit && (
                      <span className="opt-item__move">
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label="Move up"
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label="Move down"
                          disabled={i === rows.length - 1}
                          onClick={() => move(i, 1)}
                        >
                          ↓
                        </button>
                      </span>
                    )}
                    <button
                      type="button"
                      className="opt-item__summary"
                      aria-expanded={isOpen}
                      onClick={() => setSelected(isOpen ? null : i)}
                    >
                      <span className="opt-item__name">
                        {row.label.trim() || <span className="faint">Untitled option</span>}
                        {invalid && (
                          <span className="opt-item__warn" title="Needs attention">
                            ⚠
                          </span>
                        )}
                      </span>
                      <code className="opt-item__key">{row.key.trim() || '—'}</code>
                      <span className="opt-item__type">{TYPE_LABEL[row.type]}</span>
                      <span className="opt-item__default small muted">
                        default: {draftDefaultLabel(row)}
                      </span>
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="icon-btn opt-item__remove"
                        aria-label="Remove option"
                        onClick={() => {
                          setRows((r) => r.filter((_, j) => j !== i));
                          setSelected(null);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <fieldset className="opt-item__editor stack stack--tight" disabled={!canEdit}>
                        <>
                          <div className="opt-grid">
                            <div>
                              <TextField
                                label="Name shown to staff"
                                value={row.label}
                                required
                                error={rowErr.label}
                                onChange={(e) => updateLabel(i, e.currentTarget.value)}
                              />
                            </div>
                            <div>
                              <TextField
                                label="Key the product reads"
                                value={row.key}
                                required
                                mono
                                error={rowErr.key}
                                placeholder="show_cost"
                                onChange={(e) => update(i, { key: e.currentTarget.value, keyTouched: true })}
                              />
                            </div>
                            <div>
                              <SelectField
                                label="Type"
                                value={row.type}
                                onChange={(e) =>
                                  update(i, { type: e.currentTarget.value as SettingType, default: '' })
                                }
                              >
                                {(Object.keys(TYPE_LABEL) as SettingType[]).map((t) => (
                                  <option key={t} value={t}>
                                    {TYPE_LABEL[t]}
                                  </option>
                                ))}
                              </SelectField>
                            </div>
                          </div>

                          <p className="small faint">{TYPE_DESC[row.type]}</p>
                        </>

                      {row.type === 'select' && (
                        <TextField
                          label="Choices"
                          value={row.options}
                          required
                          error={rowErr.options}
                          hint="Separate with commas, e.g. basic, standard, premium"
                          onChange={(e) => update(i, { options: e.currentTarget.value })}
                        />
                      )}

                      {row.type === 'boolean' && (
                        <div className="row" style={{ gap: 'var(--s2)', alignItems: 'center' }}>
                          <span className="small">Default</span>
                          <Segmented
                            ariaLabel="Default value"
                            value={row.default === 'true' ? 'on' : 'off'}
                            options={[
                              { value: 'off', label: 'Off' },
                              { value: 'on', label: 'On' },
                            ]}
                            onChange={(v) => update(i, { default: v === 'on' ? 'true' : 'false' })}
                            disabled={!canEdit}
                          />
                          <span className="small faint">Used until a company’s licence sets it.</span>
                        </div>
                      )}

                      {(row.type === 'text' || row.type === 'number' || row.type === 'select') && (
                        <TextField
                          label="Default"
                          optional
                          type={row.type === 'number' ? 'number' : 'text'}
                          value={row.default}
                          hint="Used for any company whose licence does not set it."
                          onChange={(e) => update(i, { default: e.currentTarget.value })}
                        />
                      )}

                      <TextField
                        label="Help text"
                        optional
                        value={row.help}
                        onChange={(e) => update(i, { help: e.currentTarget.value })}
                      />
                    </fieldset>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="form-actions">
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={hasErrors}
              onClick={() => save.mutate()}
            >
              Save options
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   Company page: the values on one licence
   ========================================================================== */

interface LicenseSettingsResponse {
  productName: string;
  schema: SettingDefinition[];
  values: Record<string, SettingValue>;
  settings: Record<string, SettingValue>;
  settingsSet: string[];
}

/** How a default reads on screen: "Off", "On", "—", or the literal value. */
const showDefault = (v: SettingValue | undefined): string =>
  v === true ? 'On' : v === false ? 'Off' : v === null || v === undefined || v === '' ? '—' : String(v);

export interface LicenseOptionsHandle {
  /** Saves the per-company option values. No-op when there are no options. */
  save: () => Promise<void>;
  /** True while there is something worth saving. */
  hasOptions: boolean;
}

/**
 * The per-company option values, shown inside the licence dialog. Each option
 * is either "Default" (follows the product's default) or a value chosen for
 * this company. Saving is driven by the dialog's single Save button, through
 * the imperative handle — there is no separate button here.
 */
export const LicenseOptionsSection = forwardRef<
  LicenseOptionsHandle,
  { licenseId: string }
>(function LicenseOptionsSection({ licenseId }, ref) {
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<Record<string, SettingValue>>({});
    // Secrets are write-only: a new value (string), "remove" (null), or untouched (absent).
    const [secretDraft, setSecretDraft] = useState<Record<string, string | null>>({});

    const query = useQuery({
      queryKey: ['license-settings', licenseId],
      queryFn: ({ signal }) =>
        api.get<LicenseSettingsResponse>(`/api/staff/licenses/${licenseId}/settings`, signal),
    });

    useEffect(() => {
      if (query.data) {
        const secretKeys = new Set(query.data.schema.filter((d) => d.type === 'secret').map((d) => d.key));
        setDraft(Object.fromEntries(Object.entries(query.data.values).filter(([k]) => !secretKeys.has(k))));
        setSecretDraft({});
      }
    }, [query.data]);

    const doSave = async () => {
      const schema = query.data?.schema ?? [];
      if (schema.length === 0) return;
      const values: Record<string, SettingValue> = {};
      for (const def of schema) {
        if (def.type === 'secret') {
          if (Object.prototype.hasOwnProperty.call(secretDraft, def.key)) {
            const v = secretDraft[def.key] ?? null;
            values[def.key] = v === null || v === '' ? null : v;
          }
          continue;
        }
        const v = draft[def.key];
        values[def.key] = v === undefined || v === '' ? null : v;
      }
      await api.patch<LicenseSettingsResponse>(`/api/staff/licenses/${licenseId}/settings`, { values });
      await queryClient.invalidateQueries({ queryKey: ['license-settings', licenseId] });
    };

    useImperativeHandle(
      ref,
      () => ({ save: doSave, hasOptions: (query.data?.schema.length ?? 0) > 0 }),
      // doSave closes over draft/secretDraft/query.data, so refresh the handle
      // whenever those change.
      [draft, secretDraft, query.data, licenseId], // eslint-disable-line react-hooks/exhaustive-deps
    );

    if (query.isLoading) return null;
    const data = query.data;
    if (!data || data.schema.length === 0) return null;

    const renderRow = (def: SettingDefinition) => (
      <OptionRow
        key={def.key}
        def={def}
        stored={data.settingsSet.includes(def.key)}
        draft={draft}
        setDraft={setDraft}
        secretDraft={secretDraft}
        setSecretDraft={setSecretDraft}
      />
    );

    return (
      <div className="subsection">
        <div className="stack stack--tight">
          <strong className="subsection__title">{data.productName} options for this company</strong>
          {query.isError && <ErrorNote error={query.error} />}

          {data.schema.map(renderRow)}
        </div>
      </div>
    );
  },
);

/** One option on one row: name + the right control for its type. */
function OptionRow({
  def,
  stored,
  draft,
  setDraft,
  secretDraft,
  setSecretDraft,
}: {
  def: SettingDefinition;
  stored: boolean;
  draft: Record<string, SettingValue>;
  setDraft: Dispatch<SetStateAction<Record<string, SettingValue>>>;
  secretDraft: Record<string, string | null>;
  setSecretDraft: Dispatch<SetStateAction<Record<string, string | null>>>;
}) {
  const set = (value: SettingValue | undefined) =>
    setDraft((d) => {
      const next = { ...d };
      if (value === undefined) delete next[def.key];
      else next[def.key] = value;
      return next;
    });

  // --- Secret: Stored / Not set, with Replace and Remove -------------------
  if (def.type === 'secret') {
    const pending = secretDraft[def.key];
    const replacing = typeof pending === 'string';
    const removing = pending === null;
    const setSecret = (v: string | null | undefined) =>
      setSecretDraft((d) => {
        const next = { ...d };
        if (v === undefined) delete next[def.key];
        else next[def.key] = v;
        return next;
      });

    return (
      <div className="opt-line">
        <div className="opt-line__label">
          <span className="opt-line__name">{def.label}</span>
          {def.help && <span className="small faint">{def.help}</span>}
        </div>
        <div className="opt-line__control stack stack--tight">
          <div className="row" style={{ gap: 'var(--s2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {removing ? (
              <span className="small danger-text">Will be removed on save</span>
            ) : replacing ? (
              <span className="small">New value will be saved</span>
            ) : stored ? (
              <span className="small" title="A value is stored">
                Stored <span aria-hidden>✓</span>
              </span>
            ) : (
              <span className="small muted">Not set</span>
            )}
            {!replacing && (
              <Button size="sm" onClick={() => setSecret('')}>
                {stored ? 'Replace' : 'Set'}
              </Button>
            )}
            {stored && !removing && (
              <Button size="sm" variant="ghost" onClick={() => setSecret(null)}>
                Remove
              </Button>
            )}
            {(replacing || removing) && (
              <Button size="sm" variant="ghost" onClick={() => setSecret(undefined)}>
                Cancel
              </Button>
            )}
          </div>
          {replacing && (
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              aria-label={`New ${def.label}`}
              placeholder={`New ${def.label.toLowerCase()}`}
              value={pending}
              autoFocus
              onChange={(e) => setSecret(e.currentTarget.value)}
            />
          )}
        </div>
      </div>
    );
  }

  // --- Everything else: Default / Custom -----------------------------------
  const custom = Object.prototype.hasOwnProperty.call(draft, def.key) && draft[def.key] !== null;
  const value = draft[def.key];

  const onMode = (mode: 'default' | 'custom') => {
    if (mode === 'default') set(undefined);
    else set(def.default ?? (def.type === 'boolean' ? false : ''));
  };

  let control: ReactNode = null;
  if (custom) {
    if (def.type === 'boolean') {
      control = (
        <Segmented
          ariaLabel={def.label}
          value={value === true ? 'on' : 'off'}
          options={[
            { value: 'off', label: 'Off' },
            { value: 'on', label: 'On' },
          ]}
          onChange={(v) => set(v === 'on')}
        />
      );
    } else if (def.type === 'select') {
      control = (
        <select
          className="select"
          aria-label={def.label}
          value={String(value ?? '')}
          onChange={(e) => set(e.currentTarget.value)}
        >
          <option value="" disabled>
            Choose…
          </option>
          {(def.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    } else {
      control = (
        <input
          className="input"
          aria-label={def.label}
          type={def.type === 'number' ? 'number' : 'text'}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) =>
            set(
              def.type === 'number'
                ? e.currentTarget.value === ''
                  ? ''
                  : Number(e.currentTarget.value)
                : e.currentTarget.value,
            )
          }
        />
      );
    }
  }

  return (
    <div className="opt-line">
      <div className="opt-line__label">
        <span className="opt-line__name">{def.label}</span>
        {def.help && <span className="small faint">{def.help}</span>}
      </div>
      <div className="opt-line__control stack stack--tight">
        <div className="row" style={{ gap: 'var(--s2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Segmented
            ariaLabel={`${def.label}: default or custom`}
            value={custom ? 'custom' : 'default'}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'custom', label: 'Custom' },
            ]}
            onChange={onMode}
          />
          {!custom && (
            <span className="small muted">{showDefault(def.default)} (default)</span>
          )}
        </div>
        {control}
      </div>
    </div>
  );
}
